import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { dataService } from '../services/dataService';
import { registreerBedrijf, valideerBedrijfsnaam } from '../services/registratieService';
import logo from '../assets/logo.png';

// ─────────────────────────────────────────────────────────────────────────
//  ONBOARDING: ingelogd, maar nog geen profiel.
//
//  Hiervoor maakte AuthContext hier stil een monteursprofiel aan. Sinds
//  zelfregistratie kan dat niet meer: wie eenmaal een profiel heeft, kan
//  geen bedrijf meer starten (je eigen rol wijzigen mag niet). Dus ligt de
//  keuze bij de gebruiker: een nieuw bedrijf starten, of aansluiten bij een
//  team dat RaconWorks al gebruikt.
//
//  Bedrijf starten vereist een bevestigd e-mailadres; dat eisen de
//  beveiligingsregels (email_verified in het token). Aansluiten als
//  monteur niet, want die accounts maakt een beheerder doorgaans zelf aan.
// ─────────────────────────────────────────────────────────────────────────

export default function Onboarding({ currentUser }) {
    // Wie via "Nieuw bedrijf" op het loginscherm binnenkwam, heeft zijn keuze
    // daar al gemaakt; die hoeft hem hier niet nogmaals te maken.
    const [keuze, setKeuze] = useState(() =>
        sessionStorage.getItem('rw_onboarding_keuze') || null); // null | 'bedrijf' | 'team'
    const [bedrijfsnaam, setBedrijfsnaam] = useState('');
    const [gebruikersnaam, setGebruikersnaam] = useState('');
    const [bezig, setBezig] = useState(false);
    const [fout, setFout] = useState('');

    // Eigen status, want currentUser komt uit het moment van inloggen en
    // merkt een bevestiging in een ander tabblad niet vanzelf op.
    const [geverifieerd, setGeverifieerd] = useState(!!currentUser?.emailVerified);

    // De registratie verstuurt de eerste mail al. Hier alleen opnieuw
    // versturen als die niet is aangekomen, anders krijgt iedereen er twee.
    const stuurOpnieuw = async () => {
        setFout('');
        try {
            await sendEmailVerification(auth.currentUser);
        } catch (e) {
            setFout(e.code === 'auth/too-many-requests'
                ? 'Even geduld, er is net een mail verstuurd. Kijk ook bij ongewenste post.'
                : 'Versturen mislukt: ' + e.message);
        }
    };

    const terug = () => {
        sessionStorage.removeItem('rw_onboarding_keuze');
        setFout('');
        setKeuze(null);
    };

    const naBevestiging = async () => {
        // Het token moet het nieuwe email_verified bevatten voordat de
        // beveiligingsregels het zien; verversen forceert dat.
        await auth.currentUser.getIdToken(true);
        setGeverifieerd(true);
    };

    const controleerVerificatie = async () => {
        setFout('');
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
            await naBevestiging();
        } else {
            setFout('Nog niet bevestigd. Klik op de link in de mail en probeer opnieuw.');
        }
    };

    // De bevestigingslink opent in een ander tabblad. Dit tabblad controleert
    // daarom zelf elke paar seconden of het al gebeurd is, zodat de gebruiker
    // hier vanzelf verder kan zonder ergens op te hoeven klikken.
    useEffect(() => {
        if (keuze !== 'bedrijf' || geverifieerd) return;
        const timer = setInterval(async () => {
            try {
                await auth.currentUser.reload();
                if (auth.currentUser.emailVerified) {
                    clearInterval(timer);
                    await naBevestiging();
                }
            } catch (_) { /* even geen verbinding, volgende poging */ }
        }, 4000);
        return () => clearInterval(timer);
    }, [keuze, geverifieerd]);

    const startBedrijf = async (e) => {
        e.preventDefault();
        setFout('');
        const controle = valideerBedrijfsnaam(bedrijfsnaam);
        if (!controle.ok) { setFout(controle.fout); return; }
        setBezig(true);
        try {
            await registreerBedrijf({
                uid: currentUser.uid,
                email: currentUser.email,
                bedrijfsnaam,
                gebruikersnaam
            });
            sessionStorage.removeItem('rw_onboarding_keuze');
            // AuthContext luistert op het profiel en laadt de app vanzelf.
        } catch (err) {
            console.error('[Onboarding] Registratie mislukt:', err);
            setFout(err.code === 'permission-denied'
                ? 'Deze bedrijfsnaam is al in gebruik, of uw e-mailadres is nog niet bevestigd.'
                : (err.message || 'Er ging iets mis. Probeer het opnieuw.'));
            setBezig(false);
        }
    };

    const sluitAanBijTeam = async () => {
        setFout('');
        setBezig(true);
        try {
            // Laagste rol, geen locaties: een beheerder koppelt u daarna aan
            // de juiste locatie. Tot die tijd is de app leeg, en dat hoort.
            await dataService.saveUser({
                uid: currentUser.uid,
                email: currentUser.email
            }, 'monteur');
        } catch (err) {
            console.error('[Onboarding] Teamprofiel aanmaken mislukt:', err);
            setFout('Er ging iets mis. Probeer het opnieuw.');
            setBezig(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <div className="card w-full max-w-md p-8 fade-in">
                <div className="text-center mb-8">
                    <img src={logo} alt="Logo" className="login-logo" />
                    <h1 className="text-2xl font-bold text-white mb-2">Bijna klaar</h1>
                    <p className="text-slate-400">Ingelogd als {currentUser?.email}</p>
                </div>

                {fout && (
                    <div className="bg-red-500/10 border border-red-500/40 text-red-400 rounded-lg px-4 py-3 text-sm mb-6">
                        {fout}
                    </div>
                )}

                {keuze === null && (
                    <div className="space-y-4">
                        <button onClick={() => setKeuze('bedrijf')}
                            className="w-full text-left bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl p-5 transition-colors">
                            <p className="font-bold text-white mb-1">Nieuw bedrijf starten</p>
                            <p className="text-sm text-slate-400">U zet RaconWorks op voor uw eigen locatie en wordt
                                daarvan de beheerder.</p>
                        </button>
                        <button onClick={() => setKeuze('team')}
                            className="w-full text-left bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl p-5 transition-colors">
                            <p className="font-bold text-white mb-1">Ik hoor bij een bestaand team</p>
                            <p className="text-sm text-slate-400">Uw bedrijf gebruikt RaconWorks al. U maakt een
                                monteursprofiel aan en de beheerder geeft u daarna toegang.</p>
                        </button>
                        <button onClick={() => signOut(auth)}
                            className="w-full text-center text-sm text-slate-500 hover:text-slate-300 pt-2">
                            Uitloggen
                        </button>
                    </div>
                )}

                {keuze === 'bedrijf' && !geverifieerd && (
                    <div className="space-y-4">
                        <p className="text-slate-300">Er is een bevestigingsmail onderweg
                            naar <strong className="text-white">{currentUser?.email}</strong>. Klik op de link in
                            die mail; dit scherm gaat daarna vanzelf verder. Kijk ook bij ongewenste post.</p>
                        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-sky-400 animate-pulse"></span>
                            Wachten op bevestiging...
                        </div>
                        <button onClick={controleerVerificatie}
                            className="btn btn-primary w-full">Ik heb bevestigd</button>
                        <button onClick={stuurOpnieuw}
                            className="w-full text-center text-sm text-slate-400 hover:text-white">
                            Geen mail gekregen? Stuur opnieuw</button>
                        <button onClick={terug}
                            className="w-full text-center text-sm text-slate-500 hover:text-slate-300">Terug</button>
                    </div>
                )}

                {keuze === 'bedrijf' && geverifieerd && (
                    <form onSubmit={startBedrijf} className="space-y-4">
                        <div>
                            <label className="label">Bedrijfsnaam (wordt uw locatienaam)</label>
                            <input type="text" className="input glass-input" value={bedrijfsnaam}
                                onChange={(e) => setBedrijfsnaam(e.target.value)}
                                placeholder="Bijv. Jansen Metaal" autoFocus />
                        </div>
                        <div>
                            <label className="label">Uw naam</label>
                            <input type="text" className="input glass-input" value={gebruikersnaam}
                                onChange={(e) => setGebruikersnaam(e.target.value)}
                                placeholder="Voor collega's herkenbaar" />
                        </div>
                        <button type="submit" disabled={bezig} className="btn btn-primary w-full">
                            {bezig ? 'Bezig...' : 'Maak mijn bedrijf aan'}
                        </button>
                        <button type="button" onClick={terug}
                            className="w-full text-center text-sm text-slate-500 hover:text-slate-300">Terug</button>
                    </form>
                )}

                {keuze === 'team' && (
                    <div className="space-y-4">
                        <p className="text-slate-300">U krijgt een monteursprofiel zonder locatie. De beheerder van
                            uw bedrijf koppelt u daarna aan de juiste locatie; tot die tijd blijft de app leeg.</p>
                        <button onClick={sluitAanBijTeam} disabled={bezig} className="btn btn-primary w-full">
                            {bezig ? 'Bezig...' : 'Maak mijn profiel aan'}
                        </button>
                        <button onClick={terug}
                            className="w-full text-center text-sm text-slate-500 hover:text-slate-300">Terug</button>
                    </div>
                )}
            </div>
        </div>
    );
}
